/**
 * SidebarMenuItem
 *
 * Shared, standardized sidebar menu item component.
 * Ensures consistent icon sizing, styling, and behavior across all menu items.
 *
 * @see src/components/Sidebar.tsx - Parent component
 * @see src/constants/moduleIcons.tsx - Icon definitions
 */

import React from 'react';
import styled from 'styled-components';
import { NavLink } from 'react-router-dom';
import { ListItem, ListItemIcon, ListItemText, Chip } from '@mui/material';

/* ============================================================================
 * STYLED COMPONENTS
 * ========================================================================== */

const StyledMenuItem = styled(ListItem)<{ $active?: boolean }>`
  padding: ${(props) => props.theme.spacing(1.5, 3)};
  margin: ${(props) => props.theme.spacing(1, 2)};
  /* MUI's ListItem root is width: 100%, which with the side margins above makes
     the border-box 16px wider than the drawer's content width; the drawer's
     overflow-x: hidden then clips the pill's right border and rounded corner
     (GLX-1984). Auto lets block layout subtract the margins instead, so the
     pill is inset by the same gutter on both sides whatever the margin is. */
  width: auto;
  border-radius: ${(props) => props.theme.spacing(2)};
  cursor: pointer;
  min-height: 44px;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  position: relative;
  text-decoration: none !important;

  background: ${(props) => {
    if (props.$active) {
      return props.theme.palette.mode === 'dark'
        ? 'linear-gradient(90deg, rgba(251, 140, 0, 0.2) 0%, rgba(251, 140, 0, 0.1) 100%)'
        : 'rgba(251, 140, 0, 0.15)';
    }
    return 'transparent';
  }};

  border: ${(props) => (props.$active ? '1px solid rgba(251, 140, 0, 0.25)' : '1px solid transparent')};

  box-shadow: ${(props) =>
    props.$active
      ? `0 0 8px rgba(251, 140, 0, 0.2),
       0 0 16px rgba(251, 140, 0, 0.1),
       inset 0 0 12px rgba(251, 140, 0, 0.05)`
      : 'none'};

  &:hover {
    background: ${(props) =>
      props.theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.05)' : 'rgba(0, 0, 0, 0.04)'};
  }

  .MuiListItemIcon-root {
    min-width: 38px;
    color: ${(props) => {
      if (props.$active) return props.theme.palette.primary.main;
      return props.theme.palette.mode === 'dark'
        ? 'rgba(255, 255, 255, 0.6)'
        : 'rgba(0, 0, 0, 0.6)';
    }};
    filter: ${(props) => (props.$active ? 'drop-shadow(0 0 3px rgba(251, 140, 0, 0.3))' : 'none')};
    transition: all 0.3s ease;

    /* Standardize all icon sizes - reduced for better proportion */
    svg {
      width: 20px !important;
      height: 20px !important;
      font-size: 20px !important;
    }
  }

  .MuiListItemText-root {
    margin: 0;
    min-width: 0;

    .MuiTypography-root {
      overflow-wrap: anywhere;
      word-break: normal;
      color: ${(props) => {
        if (props.$active) return `${props.theme.palette.primary.main} !important`;
        return props.theme.palette.mode === 'dark'
          ? 'rgba(255, 255, 255, 0.85) !important'
          : 'rgba(0, 0, 0, 0.85) !important';
      }};
      font-size: 0.875rem;
      font-weight: ${(props) => (props.$active ? 600 : 500)};
      line-height: 1.5;
    }
  }
`;

const MenuItemBadge = styled(Chip)`
  height: 20px;
  font-size: 0.75rem;
  font-weight: 600;
  background: ${(props) => props.theme.palette.primary.main};
  color: ${(props) => props.theme.palette.common.white};
`;

/* ============================================================================
 * COMPONENT
 * ========================================================================== */

export interface SidebarMenuItemProps {
  /** Menu item label */
  name: string;
  /** Navigation path */
  path: string;
  /** Icon component (should be from moduleIcons.tsx) */
  icon?: React.ReactNode;
  /** Optional badge text (e.g., "NEW", "BETA") */
  badge?: string;
  /** Optional element rendered after the text (e.g., count badge) */
  rightAdornment?: React.ReactNode;
  /** Whether this menu item is currently active */
  active?: boolean;
  /** Click handler (optional, defaults to navigation) */
  onClick?: () => void;
}

/**
 * SidebarMenuItem Component
 *
 * Standardized sidebar menu item with consistent styling and icon sizing.
 * All icons are rendered at exactly 22x22px for consistency.
 */
export const SidebarMenuItem: React.FC<SidebarMenuItemProps> = ({
  name,
  path,
  icon,
  badge,
  rightAdornment,
  active = false,
  onClick,
}) => {
  return (
    <StyledMenuItem
      $active={active}
      {...({ component: NavLink, to: path } as any)}
      onClick={onClick}
    >
      {icon && <ListItemIcon>{icon}</ListItemIcon>}
      <ListItemText primary={name} />
      {badge && <MenuItemBadge label={badge} size="small" />}
      {rightAdornment}
    </StyledMenuItem>
  );
};

export default SidebarMenuItem;
